import React, { useMemo, useState } from "react";
import { message } from "antd";
import { GlobalOutlined } from "@ant-design/icons";
import xlsx from "xlsx";
import VehicleToMap from "./VehicleToMap";
import { VehicleStatusToKorean } from "../../synapse/services/job-setting.interface";
import { VehicleModelTypes } from "../../synapse/services/camp.interface";
import { VehicleStatusSettingModal } from "./VehicleStatusSettingModal";
import { DynamicPricingSettingModal } from "./DynamicPricingSettingModal";
import {
  exportVehiclesV2,
  getVehiclesMapV2,
  resolveVehicleSelectionIdsV2,
  updateVehicleDynamicPriceV2,
  updateVehicleStatusV2,
  updateVehicleWorkTargetsV2,
} from "../service/scooters.service";
import {
  TaskDefinitionDTO,
  VehicleListV2MapItem,
  VehicleListV2Selection,
} from "../service/scooter.interface";
import { ControlButton, TableTitleButtonsWrapper } from "../../common/table/table.style";
import { useLanguageContext } from "../../common/language/LanguageContext";
import { useResourceContext } from "../../common/resource/useResourceContext";
import { vehicleListV2ExcelRows } from "../service/vehicle-list-v2.utils";

interface Props {
  selection: VehicleListV2Selection | null;
  selectedCount: number;
  taskDefinitions: TaskDefinitionDTO[];
  bandNames: Map<string, string>;
  belongingNames: Map<number, string>;
  canControl: boolean;
  canDynamicPrice: boolean;
  deployedCount?: number;
  canAddTask: boolean;
  showInspect: boolean;
  onLoadLastRide: () => void;
  lastRideLoading: boolean;
  lastRideAtByVehicleId: Record<string, string | null>;
  onAddTask: () => void;
  onInspect: () => void;
  onChanged: () => void;
}

export function VehicleListV2Actions(props: Props) {
  const {
    selection,
    selectedCount,
    taskDefinitions,
    bandNames,
    canControl,
    canDynamicPrice,
    deployedCount,
    canAddTask,
    showInspect,
    onLoadLastRide,
    lastRideLoading,
    lastRideAtByVehicleId,
    onAddTask,
    onInspect,
    onChanged,
  } = props;
  const { R } = useResourceContext();
  const { languageType } = useLanguageContext();
  const [bulkVisible, setBulkVisible] = useState(false);
  const [bulkSelection, setBulkSelection] = useState<VehicleListV2Selection | null>(null);
  const [hasW1, setHasW1] = useState(false);
  const [bulkVehicles, setBulkVehicles] = useState<Array<{ vehicleId: string; imei: string; qr: string; smodel: VehicleModelTypes }>>([]);
  const [dpSelection, setDpSelection] = useState<VehicleListV2Selection | null>(null);
  const [dpImeis, setDpImeis] = useState<string[]>([]);
  const [mapItems, setMapItems] = useState<VehicleListV2MapItem[]>([]);
  const [mapVisible, setMapVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dynamicPriceVisible, setDynamicPriceVisible] = useState(false);

  const markers = useMemo(
    () => mapItems.map((item) => ({ ...item, simei: item.shortImei })),
    [mapItems]
  );

  function requireSelection(max?: number) {
    if (!selection || selectedCount === 0) {
      message.warning("기기를 선택해주세요.");
      return false;
    }
    if (max && selectedCount > max) {
      message.error(max === 30 ? "다이나믹 프라이싱 일괄 설정은 30대 이하로만 가능합니다." : "일괄조작은 2000대 이하로 선택해주세요.");
      return false;
    }
    return true;
  }

  function errorMessage(error: any) {
    return error?.response?.data?.message || error?.message || "요청을 처리하지 못했습니다.";
  }

  async function run(label: string, action: () => Promise<any>) {
    if (loading) {
      message.warning("로딩중...");
      throw new Error("로딩중...");
    }
    try {
      setLoading(true);
      const result = await action();
      if (result?.failedCount > 0) {
        if (result.processedCount > 0) onChanged();
        throw new Error(`${result.processedCount}대 처리, ${result.failedCount}대 실패했습니다. 실패한 기기를 확인해주세요.`);
      }
      message.success(label);
    } catch (error) {
      message.error(errorMessage(error));
      throw error;
    } finally {
      setLoading(false);
    }
  }

  async function openBulk(dynamicPrice = false) {
    if (!requireSelection(dynamicPrice ? 30 : 2000) || !selection || loading) return;
    try {
      setLoading(true);
      const resolved = await resolveVehicleSelectionIdsV2({ selection, includeVehicleDetails: true });
      if (!dynamicPrice && resolved.hasStatusDisabledBand) {
        message.error("상태 변경이 불가한 밴드 소속 기기가 선택됐습니다. 다시 선택해주세요.");
        return;
      }
      if (resolved.vehicleIds.length === 0) {
        message.warning("기기를 선택해주세요.");
        return;
      }
      if (resolved.vehicleIds.length > (dynamicPrice ? 30 : 2000)) {
        message.error(dynamicPrice ? "다이나믹 프라이싱 일괄 설정은 30대 이하로만 가능합니다." : "일괄조작은 2000대 이하로 선택해주세요.");
        return;
      }
      if (!resolved.vehicles || resolved.vehicles.length !== resolved.vehicleIds.length) {
        message.error("선택된 기기 정보를 불러오지 못했습니다. 다시 시도해주세요.");
        return;
      }
      if (dynamicPrice) {
        setDpSelection({ mode: "IDS", vehicleIds: [...resolved.vehicleIds] });
        setDpImeis(resolved.vehicles.map((vehicle) => vehicle.imei));
        setDynamicPriceVisible(true);
        return;
      }
      setBulkSelection({ mode: "IDS", vehicleIds: [...resolved.vehicleIds] });
      setBulkVehicles(resolved.vehicles.map((vehicle) => ({ vehicleId: vehicle.id, imei: vehicle.imei, qr: vehicle.shortImei, smodel: vehicle.smodel as VehicleModelTypes })));
      setHasW1(resolved.hasW1);
      setBulkVisible(true);
    } catch (error) {
      message.error(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  async function downloadExcel() {
    if (!requireSelection() || !selection) return;
    try {
      setLoading(true);
      const { items } = await exportVehiclesV2({ selection });
      const rows = vehicleListV2ExcelRows(items, languageType, bandNames, lastRideAtByVehicleId);
      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, xlsx.utils.json_to_sheet(rows), "Sheet1");
      xlsx.writeFile(workbook, "기기 리스트.xlsx");
    } catch (error) {
      message.error(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  async function openMap() {
    if (!selection || selectedCount === 0) {
      setMapItems([]);
      setMapVisible(true);
      return;
    }
    try {
      setLoading(true);
      const response = await getVehiclesMapV2({ selection });
      setMapItems(response.items);
      setMapVisible(true);
    } catch (error) {
      message.error(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <TableTitleButtonsWrapper>
        <div className="selection-text">{selectedCount} {R.text.selected_items}</div>
        <ControlButton type="primary" onClick={() => deployedCount == null
          ? message.warning("기기 현황을 불러오는 중입니다.")
          : window.alert(`${deployedCount}대 만큼 배치되어있습니다.`)}>
          {R.text.check_number_of_deployed_vehicles}
        </ControlButton>
        <ControlButton type="primary" danger loading={lastRideLoading} onClick={onLoadLastRide}>
          {R.text.load_last_ride_date}
        </ControlButton>
        {selectedCount > 0 && canControl && (
          <ControlButton type="primary" danger loading={loading} onClick={() => openBulk()}>
            일괄 조작
          </ControlButton>
        )}
        {canAddTask && (
          <ControlButton ghost type="primary" danger onClick={onAddTask}>
            작업정의 추가
          </ControlButton>
        )}
        {selectedCount > 0 && <ControlButton loading={loading} onClick={downloadExcel}>{R.text.export}</ControlButton>}
        {showInspect && (
          <ControlButton type="primary" ghost onClick={onInspect}>
            {R.text.full_inspection}
          </ControlButton>
        )}
        <ControlButton icon={<GlobalOutlined />} loading={loading} onClick={openMap}>
          TO MAP
        </ControlButton>
        {selectedCount > 0 && canDynamicPrice && (
          <ControlButton type="primary" danger loading={loading} onClick={() => openBulk(true)}>
            일괄 DP 설정
          </ControlButton>
        )}
      </TableTitleButtonsWrapper>

      {bulkVisible && bulkSelection && <VehicleStatusSettingModal
        visible={bulkVisible}
        close={() => setBulkVisible(false)}
        fetch={onChanged}
        vehicles={bulkVehicles}
        includesW1={hasW1}
        taskDefinitionsOverride={taskDefinitions}
        onSetVehicleStatus={(type) => run("설정성공", () => updateVehicleStatusV2({ selection: bulkSelection, type, title: VehicleStatusToKorean[type] }))}
        onSetTaskStatus={(workDefinitionId) => run("설정성공", () => updateVehicleWorkTargetsV2({ selection: bulkSelection, operation: "SET", workDefinitionId }))}
        onResetTaskStatus={() => run("초기화성공", () => updateVehicleWorkTargetsV2({ selection: bulkSelection, operation: "CLEAR" }))}
      />}

      {mapVisible && <VehicleToMap viewType="map" changeViewType={() => setMapVisible(false)} selectedRows={markers} />}

      {dynamicPriceVisible && dpSelection && <DynamicPricingSettingModal
        visible={dynamicPriceVisible}
        close={() => setDynamicPriceVisible(false)}
        fetch={onChanged}
        target={dpImeis}
        onSetDynamicPrice={(ratio, activeHours) => run("DP 설정 성공", () => updateVehicleDynamicPriceV2({ selection: dpSelection, ratio, activeHours }))}
      />}
    </>
  );
}
